import {useNavigate, useParams} from "react-router-dom";
import {useEffect, useMemo, useRef, useState} from "react";

import {ArrowLeft, ClipboardList, Clock, FileText, MessageCircle, MoreVertical, NotebookPen,} from "lucide-react";
import {SamTabedPage} from "../../../components/SamTabedPaged.tsx";
import {useStaffApi} from "../../../context/ApiContext.tsx";
import {convertKebabToEnumAction} from "../../../utils/kebabhelper.ts";
import Tooltip from "../../../components/Tooltip.tsx";
import {AvailableAction, StaffOrderDetails} from "../../../api/ApiTypes.ts";
import {OrderDetailsSkeleton} from "../../appraiser/orderdetails/OrderDetailsSkeleton.tsx";
import {HistoryTab} from "../../appraiser/orderdetails/HistoryTab.tsx";
import {useAuth} from "../../../context/AuthContext.tsx";
import {FilesTab} from "../../appraiser/orderdetails/FilesTab.tsx";
import {MessagesTab} from "../../appraiser/orderdetails/MessagesTab.tsx";
import {StaffOrderDetailsTab} from "./StaffOrderDetailsTab.tsx";

export const StaffOrderDetailsLayout = () => {
    const navigate = useNavigate();
    const staffApi = useStaffApi();
    const { id, action } = useParams(); // Get the ID from URL parameters
    const me = useAuth();

    const [selectedTabIndex, setSelectedTabIndex] = useState(0); // Default to the first tab
    // State for dynamic modal
    const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
    const [myOrderDetails, setMyOrderDetails] = useState<StaffOrderDetails>();
    const [urlParamsActionProcessed, setUrlParamsActionProcessed] = useState(false);


    // Actions dropdown state
    const [isActionsDropdownOpen, setActionsDropdownOpen] = useState(false);
    const actionsDropdownRef = useRef<HTMLDivElement | null>(null);

    const refreshOrderDetails = async () => {
        if (!id) return;
        setOrderDetailsLoading(true);
        try {
            const details = await staffApi.getStaffOrderDetails(id);
            setMyOrderDetails(details);
        }
        catch (error) {
            console.error("Failed to fetch order details:", error);
        }
        finally {
            setOrderDetailsLoading(false);
        }
    }

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent): void {
            if (actionsDropdownRef.current &&
                !actionsDropdownRef.current.contains(event.target as Node)) {
                setActionsDropdownOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        refreshOrderDetails();
    }, []);

// effect to detect url params and either open a modal or open a tab
    useEffect(() => {
        // Only run when order is loaded and action hasn't been processed yet
        if (!orderDetailsLoading && myOrderDetails?.orderDetails?.appraisal?.id && action && !urlParamsActionProcessed) {
            //try modal actions first
            const actionToPerform = convertKebabToEnumAction(action);
            if (actionToPerform && myOrderDetails.availableActions &&
                myOrderDetails.availableActions.includes(actionToPerform)) {
                handleActionSelect(actionToPerform);
                setUrlParamsActionProcessed(true);
            }
            //no modal action found, try to open a tab
            else {
                const tabIndex = tabComponents.findIndex(tab => tab.slug === action.toLowerCase());
                if (tabIndex != -1) {
                    setSelectedTabIndex(tabIndex);
                }
                setUrlParamsActionProcessed(true);
            }
        }
    }, [myOrderDetails, orderDetailsLoading, action, id, urlParamsActionProcessed]);



    // Handle back navigation
    const handleBack = () => {
        navigate("/staff/dashboard");
    };


    // Handle action selection from dropdown
    const handleActionSelect = (action: AvailableAction | string): void => {
        setActionsDropdownOpen(false);

        switch (action) {
            case AvailableAction.QC_FORM:
                window.open(`/staff/orderdetails/${myOrderDetails?.orderDetails?.appraisal?.id}/qc`, "_blank");
                break;
            case AvailableAction.FILL_OUT_FORM:
                window.open(`/staff/orderdetails/${myOrderDetails?.orderDetails?.appraisal?.id}/fill-form`, "_blank");
                break;
            case AvailableAction.VIEW_FORM_READ_ONLY:
                window.open(`/staff/orderdetails/${myOrderDetails?.orderDetails?.appraisal?.id}/view-form-read-only`, "_blank");
                break;
            default:
                console.log("Unhandled action:", action);
        }
    };

    // Toggle dropdown visibility
    const toggleActionsDropdown = () => {
        setActionsDropdownOpen(!isActionsDropdownOpen);
    };

    // Memoize the tab components so they don't rerender unless myOrderDetails changes
    const tabComponents = useMemo(() => {
        return [
            {
                name: 'Order Details',
                icon: <FileText className="h-5 w-5" />,
                slug: 'order-details',
                description: 'View order information and property details',
                component: () => <StaffOrderDetailsTab orderDetails={myOrderDetails?.orderDetails} />
            },
            {
                name: 'History & Revisions',
                slug: 'histories',
                icon: <Clock className="h-5 w-5" />,
                description: 'View order history and revisions',
                component: () => <HistoryTab orderId={myOrderDetails?.orderDetails?.appraisal?.id} histories={myOrderDetails?.orderDetails?.histories} userType={me?.me?.userType} />
            },
            {
                name: 'Files',
                slug: 'files',
                icon: <ClipboardList className="h-5 w-5" />,
                description: 'Access and manage order files and documents',
                component: () => <FilesTab orderId={myOrderDetails?.orderDetails?.appraisal?.id} files={myOrderDetails?.orderDetails?.files} userType={me?.me?.userType} />
            },
            {
                name: 'Messages',
                slug: 'messages',
                icon: <MessageCircle className="h-5 w-5" />,
                description: 'View and send messages related to this order',
                component: () => <MessagesTab orderId={myOrderDetails?.orderDetails?.appraisal?.id} messages={myOrderDetails?.orderDetails?.messages}  />
            }
        ];
    }, [myOrderDetails]); // Only recreate when myOrderDetails changes

    if (orderDetailsLoading) {
        return <OrderDetailsSkeleton/>
    }

    if (!myOrderDetails) {
        return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mt-4">
                <h2 className="text-yellow-700 font-medium">Order Not Found</h2>
                <p className="text-yellow-600 mt-1"> The requested order details could not be found.</p>
                <button
                    onClick={handleBack}
                    className="mt-3 inline-flex items-center px-3 py-2 border border-yellow-300 text-sm leading-4 font-medium rounded-md text-yellow-700 bg-white hover:bg-yellow-50"
                >
                    Return to Orders
                </button>
            </div>
        );
    }

    // Action buttons with dropdown - Only show when there are available actions
    const hasAvailableActions = myOrderDetails.availableActions &&
        Array.isArray(myOrderDetails.availableActions) &&
        myOrderDetails.availableActions.length > 0;

    return (
        <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Header with back button, title, and action buttons - Improved for mobile */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 space-y-4 sm:space-y-0">
                <div className="flex items-center">
                    <Tooltip text='Back'>
                        <button
                            onClick={handleBack}
                            className="mr-4 text-slate-600 hover:text-blue-600 transition-colors duration-200"
                            aria-label="Back to dashboard"
                        >
                            <ArrowLeft className="h-8 w-8 text-blue-700 hover:cursor-pointer" />
                        </button>
                    </Tooltip>
                    <div className="overflow-hidden">
                        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 truncate">
                            Order #{myOrderDetails?.orderDetails?.appraisal?.usaFileNumber}
                        </h1>
                        <p className="text-xs sm:text-sm text-slate-500 truncate">
                            Order ID: {myOrderDetails?.orderDetails?.appraisal?.id}
                        </p>
                    </div>
                </div>

                {/* Action buttons with dropdown - Mobile responsive */}
                <div className="flex flex-wrap gap-2 sm:space-x-3 justify-end">
                    {hasAvailableActions && (
                        <div className="relative" ref={actionsDropdownRef}>
                            <button
                                onClick={toggleActionsDropdown}
                                className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:cursor-pointer hover:bg-blue-700 shadow-sm"
                            >
                                <span className="hidden sm:inline">Actions</span>
                                <span className="sm:hidden">Actions</span>
                                <MoreVertical className="h-4 w-4 ml-1 sm:ml-2" />
                            </button>

                            {isActionsDropdownOpen && (
                                <div className="absolute right-0 mt-2 w-48 sm:w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                    <div className="py-1" role="menu" aria-orientation="vertical">
                                        {myOrderDetails.availableActions.includes(AvailableAction.QC_FORM) && (
                                            <button
                                                onClick={() => handleActionSelect(AvailableAction.QC_FORM)}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center"
                                                role="menuitem"
                                            >
                                                <NotebookPen className="h-4 w-4 mr-2" /> Qc Form
                                            </button>
                                        )}
                                        {myOrderDetails.availableActions.includes(AvailableAction.FILL_OUT_FORM) && (
                                            <button
                                                onClick={() => handleActionSelect(AvailableAction.FILL_OUT_FORM)}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center"
                                                role="menuitem"
                                            >
                                                <NotebookPen className="h-4 w-4 mr-2" /> Fill Out Form
                                            </button>
                                        )}
                                        {myOrderDetails.availableActions.includes(AvailableAction.VIEW_FORM_READ_ONLY) && (
                                            <button
                                                onClick={() => handleActionSelect(AvailableAction.VIEW_FORM_READ_ONLY)}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center"
                                                role="menuitem"
                                            >
                                                <NotebookPen className="h-4 w-4 mr-2" /> View Form Read Only
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>

            {/* Tabs layout with memoized tabs */}
            <SamTabedPage
                tabs={tabComponents}
                selectedTabIndex={selectedTabIndex}
                setSelectedTabIndex={setSelectedTabIndex}
            />
        </div>
    );
};